import { useEffect, useSyncExternalStore } from 'react'
import {
  getSessionFollowingSummary,
  type ConsentGiverSummary,
  type FollowedMemberSummary,
} from '../features/sessions/data/sessions-api-client'

/**
 * Whether the signed-in Independent follows anyone's sessions (server-derived
 * from the per-association session-follow permission — never a hardcoded
 * role). Gates the Sessions nav + route for Independents and feeds the
 * follower empty state. Fails CLOSED: an error reads as "not following".
 *
 * Keyed by user id: a snapshot for another user always reads as idle, and a
 * response that lands after an account switch is dropped. A failed first
 * load retries with backoff; loaded data is revalidated in the background
 * when the app regains focus (so an admin's follow toggle reaches the
 * Independent without a reload) and kept if that refresh fails.
 */
export type SessionFollowingState = {
  status: 'idle' | 'loading' | 'loaded' | 'error'
  userId: string | null
  followedMembers: FollowedMemberSummary[]
  /** Members this Independent may give session-recording consent for. */
  consentGiverFor: ConsentGiverSummary[]
  /** Epoch ms of the last successful load. */
  loadedAt?: number
}

const IDLE: SessionFollowingState = { status: 'idle', userId: null, followedMembers: [], consentGiverFor: [] }

/** Backoff between automatic retries of a failed first load. */
export const SESSION_FOLLOWING_RETRY_DELAYS_MS = [2_000, 5_000, 15_000] as const
/** Revalidate on focus / visibility once the data is at least this old. */
export const SESSION_FOLLOWING_STALE_MS = 60_000

let state: SessionFollowingState = IDLE
let inflight: Promise<void> | null = null
let retryTimer: ReturnType<typeof setTimeout> | null = null
let retryAttempt = 0
const listeners = new Set<() => void>()

function setState(next: SessionFollowingState) {
  state = next
  for (const listener of listeners) listener()
}

function clearRetry() {
  if (retryTimer) clearTimeout(retryTimer)
  retryTimer = null
}

function onAppFocus() {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
  if (state.status !== 'loaded' || !state.userId) return
  if (Date.now() - (state.loadedAt ?? 0) < SESSION_FOLLOWING_STALE_MS) return
  void refreshSessionFollowing()
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0 && typeof window !== 'undefined') {
    window.addEventListener('focus', onAppFocus)
    document.addEventListener('visibilitychange', onAppFocus)
  }
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('focus', onAppFocus)
      document.removeEventListener('visibilitychange', onAppFocus)
    }
  }
}

function getState(): SessionFollowingState {
  return state
}

function load(userId: string, background: boolean): Promise<void> {
  if (!background) {
    const same = state.userId === userId
    setState({
      status: 'loading',
      userId,
      followedMembers: same ? state.followedMembers : [],
      consentGiverFor: same ? state.consentGiverFor : [],
    })
  }
  const request = getSessionFollowingSummary()
    .then(({ followedMembers, consentGiverFor }) => {
      if (state.userId !== userId) return
      retryAttempt = 0
      setState({ status: 'loaded', userId, followedMembers, consentGiverFor, loadedAt: Date.now() })
    })
    .catch(() => {
      if (state.userId !== userId) return
      // A failed background refresh keeps what we already know.
      if (background && state.status === 'loaded') return
      setState({ status: 'error', userId, followedMembers: [], consentGiverFor: [] })
      const delay = SESSION_FOLLOWING_RETRY_DELAYS_MS[retryAttempt]
      if (delay != null) {
        retryAttempt += 1
        clearRetry()
        retryTimer = setTimeout(() => {
          retryTimer = null
          if (state.userId === userId && state.status === 'error') void load(userId, false)
        }, delay)
      }
    })
    .finally(() => {
      if (inflight === request) inflight = null
    })
  inflight = request
  return request
}

/** Fetch the follow summary for `userId` unless already loaded/loading for them. */
export function ensureSessionFollowingLoaded(userId: string, opts: { force?: boolean } = {}): Promise<void> {
  if (!opts.force && state.userId === userId && state.status !== 'idle') {
    return inflight ?? Promise.resolve()
  }
  if (state.userId !== userId) {
    clearRetry()
    retryAttempt = 0
  }
  return load(userId, false)
}

/** Manual retry (error state "Retry" button): restart the backoff too. */
export function retrySessionFollowing(): Promise<void> {
  if (!state.userId) return Promise.resolve()
  clearRetry()
  retryAttempt = 0
  return load(state.userId, false)
}

/**
 * Re-fetch for the tracked user, keeping loaded data on screen meanwhile
 * (focus revalidation — how an admin's follow toggle reaches the Independent).
 * No-op when idle.
 */
export function refreshSessionFollowing(): Promise<void> {
  if (!state.userId) return Promise.resolve()
  if (state.status !== 'loaded') return retrySessionFollowing()
  if (inflight) return inflight
  return load(state.userId, true)
}

/** The user the store is tracking (capture before a request that may outlive it). */
export function sessionFollowingUserId(): string | null {
  return state.userId
}

/**
 * Fresh follow list from the sessions list response (same server truth).
 * Pass the `userId` captured when the request started: a response for a user
 * who has since signed out or switched accounts is ignored.
 */
export function updateSessionFollowingFromList(
  followedMembers: FollowedMemberSummary[],
  userId: string | null = state.userId,
): void {
  if (!state.userId || userId !== state.userId) return
  clearRetry()
  retryAttempt = 0
  // The list response carries no consent marks: keep the last known ones.
  setState({
    status: 'loaded',
    userId: state.userId,
    followedMembers,
    consentGiverFor: state.consentGiverFor,
    loadedAt: Date.now(),
  })
}

/** Sign-out / account switch: forget the previous user's follow list. */
export function resetSessionFollowing(): void {
  clearRetry()
  retryAttempt = 0
  inflight = null
  setState(IDLE)
}

/**
 * Subscribe to the follow summary for `userId`. Pass `null` to skip loading
 * (e.g. the caller is not an Independent); the result then reads as idle.
 */
export function useSessionFollowing(userId: string | null): SessionFollowingState {
  const snapshot = useSyncExternalStore(subscribe, getState, getState)
  useEffect(() => {
    if (userId) void ensureSessionFollowingLoaded(userId)
  }, [userId])
  if (!userId || snapshot.userId !== userId) return IDLE
  return snapshot
}

/** True once loaded with at least one followed member. */
export function followsAnySessions(s: SessionFollowingState): boolean {
  return s.status === 'loaded' && s.followedMembers.length > 0
}

/** True once loaded with at least one member this Independent may give consent for. */
export function givesConsentForAnyone(s: SessionFollowingState): boolean {
  return s.status === 'loaded' && s.consentGiverFor.length > 0
}
